<template>
  <VBusHelper
    :reactOn="$constants.EVENTS.SERVICES.INITIATION.REQUEST_OTP"
    :handler="
      () => {
        otpCode = '';
        modalRef.show();
      }
    "
  />

  <DialogComponent
    ref="modalRef"
    type="modal"
    @closed="
      ({ source }) => {
        source !== 'filledButton' && !otpSubmitted &&
          $bus.emit($constants.EVENTS.SERVICES.INITIATION.INITIATION_FAILED, {
            messageTitle: 'Transaction annulée',
            message: 'Vous avez annulé la transaction'
          });
      }
    "
  >
    <div class="flex flex-col gap-6 p-2 overflow-y-auto max-h-[80vh]">
      <div class="flex flex-col gap-2 text-center">
        <h3 class="text-2xl font-bold text-gray-800">Code d'autorisation OTP</h3>
        <div v-if="instructions.title" class="text-gray-500 text-sm">
          {{ instructions.title }}
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <!-- USSD & Buttons (Matched from ManualPaymentInstructions) -->
        <div v-if="instructions.ussdCode" class="flex flex-col gap-2">
          <div
            class="flex gap-1 items-center border justify-center bg-gray-100 rounded-xl p-4 text-center font-mono text-lg select-all cursor-pointer"
            @click="copyUssdCode"
          >
            {{ instructions.ussdCode }}
          </div>
          
          <div class="flex gap-2">
            <button 
              @click="copyUssdCode"
              class="flex-1 bg-white border border-gray-200 rounded-xl py-2 px-3 text-[10px] font-bold text-gray-600 flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
            >
              📋 COPIER LE CODE
            </button>
            <button 
              @click="$bus.emit($constants.EVENTS.SHARED.LAUNCHED_USSD, { ussdCode: instructions.ussdCode })"
              class="flex-1 bg-primary-600 rounded-xl py-2 px-3 text-[10px] font-bold text-white flex items-center justify-center gap-2 hover:bg-primary-700 transition-colors"
            >
              📞 APPELER
            </button>
          </div>
        </div>

        <!-- Instructions List -->
        <div class="flex flex-col gap-3">
          <div
            v-for="(instruction, index) in instructions.instructions || []"
            :key="index"
            class="flex flex-col gap-2 text-sm"
          >
            <div
              class="flex justify-between"
              :class="{ 'font-bold underline': instruction.action }"
              @click="onInstructionClicked(instruction)"
              v-if="
                index !== 0 ||
                (instruction.action !== 'copy' && instruction.action !== 'tel')
              "
            >
              <span class="flex-1">
                &bull;
                <span v-html="instruction.text"></span>
              </span>
              <div class="flex-shrink-0 ml-2">
                <span v-if="instruction.action == 'tel'">📞</span>
                <span v-else-if="instruction.action == 'copy'">📋</span>
              </div>
            </div>

            <div v-if="instruction.type === 'double'" class="grid grid-cols-2 gap-3 mt-1">
               <div v-if="instruction.option1" class="buttonComponent text-center py-2 px-1 text-xs cursor-pointer" @click="onInstructionClicked(instruction.option1)">
                <span v-html="instruction.option1.text"></span>
              </div>
              <div v-if="instruction.option2" class="buttonComponent text-center py-2 px-1 text-xs cursor-pointer" @click="onInstructionClicked(instruction.option2)">
                <span v-html="instruction.option2.text"></span>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2 mt-2">
          <label class="text-sm font-semibold text-gray-600 ml-1">Code OTP</label>
          <input
            v-model="otpCode"
            type="number"
            inputmode="numeric"
            placeholder="Entrez le code"
            class="w-full bg-gray-50 border border-gray-200 rounded-2xl px-5 py-4 text-xl font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
            autofocus
            @keyup.enter="submitOtp"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3 mt-2">
        <button 
          class="buttonComponent w-full py-4 text-lg font-bold shadow-lg shadow-primary-200" 
          @click="submitOtp"
          :disabled="!otpCode || otpCode.length < 4"
        >
          Confirmer le paiement
        </button>
        <button 
          class="text-gray-400 text-sm font-medium py-2 hover:text-gray-600 transition-colors"
          @click="modalRef.hide()"
        >
          Annuler
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import Commons from '../../shared/libraries/commons';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const modalRef = ref(null);
const otpCode = ref('');
const otpSubmitted = ref(false);

const instructions = computed(() => {
  const store = $stores.services;
  const appData = $stores.appData;
  const userData = $stores.user.state.current;
  const countryCode = userData?.countryCode;
  
  const operationData = store.state.transactionInitiation;
  const operator = operationData.phoneOperator;
  
  const allManualInstructions = appData.state.data.manualOperationsInstructions || [];
  const manualInstruction = allManualInstructions.find((instruction) => {
    let countryMatch = instruction.country == countryCode;
    let operatorMatch = (instruction.operator || "").toUpperCase() == (operator || "").toUpperCase();
    return countryMatch && operatorMatch;
  });
  
  if (manualInstruction) {
     let stringInstrucctions = JSON.stringify(manualInstruction);
     stringInstrucctions = stringInstrucctions.replace(
       /montant/g,
       operationData.senderAmount || ''
     );
     stringInstrucctions = stringInstrucctions.replace(
       /{amount}/g,
       operationData.senderAmount || ''
     );
     return JSON.parse(stringInstrucctions);
  }
  
  return {};
});

const copyUssdCode = () => {
  if (instructions.value.ussdCode) {
    Commons.copyToClipboard(
      instructions.value.ussdCode,
      () => {
        $bus.emit($constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Code copiée',
          body: instructions.value.ussdCode
        });
      },
      () => {
        $bus.emit($constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Code non copiée',
          body: 'veuillez réessayer'
        });
      }
    );
  }
};

const onInstructionClicked = (option) => {
  if (!option.action) return;

  if (option.action === 'tel') {
    $bus.emit($constants.EVENTS.SHARED.LAUNCHED_USSD, {
      ussdCode: option.actionContent
    });
  } else if (option.action === 'copy') {
    Commons.copyToClipboard(option.actionContent);
  }
};

const submitOtp = () => {
  if (!otpCode.value || otpCode.value.length < 4) return;
  
  otpSubmitted.value = true;
  modalRef.value.hide();
  $bus.emit($constants.EVENTS.SERVICES.INITIATION.OTP_SUBMITTED, otpCode.value);
  
  // Reset for next time
  setTimeout(() => {
    otpSubmitted.value = false;
  }, 500);
};
</script>

<style scoped>
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
}
</style>
